import { Injectable, Injector, OnDestroy, Renderer2, RendererFactory2 } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of as observableOf, Subject } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ColorConfig, ThemeableComponentConfig, ThemeableComponentName, ThemeConfig, ThemeMainColorType } from './_theme/theme-config';
// import * as tinycolor from 'tinycolor2';
import { LoggingService } from './logging.service';
import { wrapAllMethodsWithLogging } from 'src/app/core/decorators/log-all-errors.decorator';

import tinycolor from 'tinycolor2';
@Injectable({
  providedIn: 'root'
})
export class ThemingService implements OnDestroy {
  myMethod$: Observable<any>;
  private myMethodSubject = new Subject<any>();
  ThemeConfigData: any;
  private static readonly THEME_VARIABLE_PREFIX = '--theme';
  currentTheme: Subject<ThemeConfig> = new Subject();
  currentLogo: BehaviorSubject<ThemeConfig> = new BehaviorSubject(null);
  private readonly renderer: Renderer2;
  componentName: string = 'themingService';

  constructor(private rendererFactory: RendererFactory2,
    private httpClient: HttpClient,
    private injector: Injector) {
    wrapAllMethodsWithLogging(this, this.componentName, this.injector, LoggingService);
    this.myMethod$ = this.myMethodSubject.asObservable();
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  async myMethod(data) {
    this.myMethodSubject.next(data);
  }
  ngOnDestroy(): void {
    this.renderer?.destroy();
  }

  /**
   * The load method must return a Promise, since that will make the application wait in the APP_INITIALIZER DI token
   * After all the loading and set up is finished, we can proceed with rendering the application
   */
  initialize() {
    this.myMethod$.subscribe(res => {
      this.ThemeConfigData = res;
      this.subscribeToThemeChanges();
      return observableOf(this.ThemeConfigData as ThemeConfig)
        .pipe(
          catchError(() => observableOf(this.ThemeConfigData)),
          tap((themeConfig) => {
            this.currentTheme.next(themeConfig)
            this.currentLogo.next(themeConfig)
          })
        )
        .toPromise();
    })
  }

  private subscribeToThemeChanges(): void {
    this.currentTheme.subscribe((themeConfig: ThemeConfig) => {
      this.setupMainPalettes(themeConfig);
      // this.setupComponentVariables(themeConfig);
    });
  }

  /**
   * This method will generate the theme palette required by Angular Material
   * It will correctly set up the variable names used in /assets/styles/_variables.scss
   * @param themeConfig
   * @private
   */
  private setupMainPalettes(themeConfig: ThemeConfig): void {
    Object.keys(themeConfig.selfpwa).forEach((key: string) => {
      const selectedColorValue: string = themeConfig.selfpwa[key as ThemeMainColorType];
      const variableName: string = this.prependVariableName(this.convertCamelCaseToKebabCase(key));
      const colorPalette: Array<ColorConfig> = this.generateColorPalette(selectedColorValue);
      colorPalette.forEach((colorConfig: ColorConfig) => {
        const { colorVariant, colorHexValue, shouldHaveDarkContrast } = colorConfig;

        // Set the color variable
        const colorVariableName = `${variableName}-${colorVariant}`;
        // console.log("colorVariableName", colorVariableName)
        this.setColorVariable(colorVariableName, colorHexValue);
      });
    });
  }

  /**
   * This method generates a color palette comprised of 14 main and 14 contrast colors per the Angular material specification
   * It will allow us to have different shades of some color and we can use all of those shades in our material and non-material
   * components via css.
   * The configuration can never be 100% accurate to the Material stock colors, as they are sometimes hand-made by a designer
   * So this calculation will never be 100% accurate to the original colors provided in the Material design CSS files
   * @param hexColor
   * @private
   */
  private generateColorPalette(hexColor: string): Array<ColorConfig> {
    // console.log(hexColor)
    const defaultcolor = tinycolor(hexColor);
    const defaultcolor5 = tinycolor(hexColor);
    const primarylighter5 = defaultcolor5.desaturate(5).lighten(10);
    const defaultcolor10 = tinycolor(hexColor);
    const primarylighter10 = defaultcolor10.desaturate(10).lighten(20);

    const defaultcolortodark5 = tinycolor(hexColor);
    const primarydarker5 = defaultcolortodark5.desaturate(5).darken(10);
    const defaultcolortodark10 = tinycolor(hexColor);
    const primarydarker510 = defaultcolortodark10.desaturate(10).darken(20);

    return [
      this.mapColorConfig(defaultcolor, 'default'),
      this.mapColorConfig(primarylighter5, 'primary-color-lighter-5'),
      this.mapColorConfig(primarylighter10, 'primary-color-lighter-10'),
      this.mapColorConfig(primarydarker5, 'primary-color-darker-5'),
      this.mapColorConfig(primarydarker510, 'primary-color-darker-10'),
    ];
  }

  rgbToHex(r: number, g: number, b: number): string {
    const toHex = (value: number) => value.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  /**
   * Map the color and its variant to something that we understand
   * Also check if we need to use a light or dark contrast color
   * @param tinyColorInstance
   * @param colorVariant
   * @private
   */
  private mapColorConfig(tinyColorInstance: tinycolor.Instance, colorVariant: string): ColorConfig {
    return {
      colorVariant,
      colorHexValue: tinyColorInstance.toHexString(),
      shouldHaveDarkContrast: tinyColorInstance.isLight()
    };
  }

  private multiply(rgb1: tinycolor.ColorFormats.RGB, rgb2: tinycolor.ColorFormats.RGB): tinycolor.Instance {
    rgb1.r = Math.floor((rgb1.r * rgb2.r) / 255);
    rgb1.g = Math.floor((rgb1.g * rgb2.g) / 255);
    rgb1.b = Math.floor((rgb1.b * rgb2.b) / 255);
    const { r, g, b } = rgb1;

    return tinycolor(`rgb ${r} ${g} ${b}`);
  }

  /**
   * Generate the css variables that the components will use in the theming process
   * @param theme
   * @private
   */
  private setupComponentVariables(theme: ThemeConfig): void {
    Object.keys(theme.components).forEach((componentName: string) => {
      const componentConfig: ThemeableComponentConfig = theme.components[componentName as ThemeableComponentName];
      Object.keys(componentConfig.colors).forEach((property: string) => {
        const cssVariable = this.prependVariableName(
          this.convertCamelCaseToKebabCase(`${componentName}-${property}`));
        // @ts-ignore
        const colorHex = componentConfig.colors[property];
        const colorHSL = tinycolor(colorHex).toHsl();

        this.setHSLVariables(cssVariable, colorHSL);
        this.setColorVariable(cssVariable, tinycolor(colorHex).toHslString());
      });
    });
  }

  private setHSLVariables(variableName: string, hsl: tinycolor.ColorFormats.HSLA): void {
    // we can use these variables to create lighter / darker shades by modifying the lightness value
    // can't do that with scss since the variables are generated on runtime
    this.setColorVariable(`${variableName}-h`, `${Math.round(hsl.h)}`);
    this.setColorVariable(`${variableName}-s`, `${Math.round(hsl.s * 100)}%`);
    this.setColorVariable(`${variableName}-l`, `${Math.round(hsl.l * 100)}%`);
  }

  private prependVariableName(key: string): string {
    return `${ThemingService.THEME_VARIABLE_PREFIX}-${key}`;
  }

  /**
   * Change a camelCase variable to a kebab case
   * e.g: primaryColor -> primary-color
   * @param key
   * @private
   */
  private convertCamelCaseToKebabCase(key: string): string {
    return key.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
  }

  private setColorVariable(variable: string, color: string): void {
    // console.log("variable", variable, color)
    document.documentElement.style.setProperty(variable, color);
  }
}